import { useRestApiProvider } from "@jugl-web/rest-api";
import { Alert, PlainButton, Menu } from "@jugl-web/ui-kit";
import { cx, useToast, useTranslations } from "@jugl-web/utils";
import { collapsedIconButtonClassName } from "@web-src/modules/client-support/pages/ClientSupportPage/consts";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useMemo, useState } from "react";
import { ReactComponent as MenuIcon } from "./assets/menu.svg";
import { ReactComponent as DisconnectIcon } from "./assets/disconnect.svg";

export const DomainActionsCell: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { emailAccount } = useEmailAccount();
  const { integrationsApi } = useRestApiProvider();
  const [updateEmailConfig, { isLoading: isUpdatingEmailConfig }] =
    integrationsApi.useUpdateEmailConfigMutation();

  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);

  const handleDisconnectAccount = useCallback(async () => {
    try {
      await updateEmailConfig({
        entityId,
        data: {
          ai_reply: false,
          metadata: {
            ...emailAccount?.metadata,
            client_email: "",
            domain_auth_enabled: false,
            domain_auth_verified: false,
            email_forwarding_verified: false,
            domain: "",
          },
        },
        meta: { silent: true },
      }).unwrap();

      toast(
        t({
          id: "client-support-page.email-was-disconnected",
          defaultMessage: "Email was disconnected",
        })
      );
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [emailAccount, entityId, t, toast, updateEmailConfig]);

  const menuItems = useMemo(
    () => [
      [
        {
          id: "disconnect",
          label: t({
            id: "common.disconnect",
            defaultMessage: "Disconnect",
          }),
          icon: <DisconnectIcon />,
          onSelect: () => setIsDisconnectAlertOpen(true),
          isDisabled: isUpdatingEmailConfig,
        },
      ],
    ],
    [isUpdatingEmailConfig, t]
  );

  return (
    <>
      <Menu
        placement="bottom"
        renderTrigger={({ Trigger, triggerRef }) => (
          <Trigger
            ref={triggerRef}
            as={PlainButton}
            className={cx(collapsedIconButtonClassName, "h-8 w-8")}
          >
            <MenuIcon />
          </Trigger>
        )}
        autoClose
        sections={menuItems}
      />
      <Alert
        isOpen={isDisconnectAlertOpen}
        onRequestClose={() => setIsDisconnectAlertOpen(false)}
        title={t({
          id: "client-support-page.disconnect-account",
          defaultMessage: "Disconnect Account",
        })}
        content={t(
          {
            id: "client-support-page.disconnect-email-account",
            defaultMessage:
              "Are you sure you want to disconnect {email} Email account?",
          },
          {
            email: (
              <span className="font-medium text-primary">
                {emailAccount?.metadata?.client_email}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "client-support-page.disconnect",
              defaultMessage: "Disconnect",
            }),
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              await handleDisconnectAccount();
            },
          },
        ]}
      />
    </>
  );
};
